import React, { useEffect, useState } from "react";
import Layout from "../../layout/Layout";
import { useDispatch, useSelector } from "react-redux";
import { createKbArticle, updateKbArticle } from "../../Redux/Action/KbAction";
import LoadingOverlay from "react-loading-overlay-ts";
import { useNavigate, useParams } from "react-router-dom";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import Api from "../../Config/ApiConfig";
import { toast } from "react-toastify";

export default function KnowledgeBaseForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    category: "General",
    targetDomain: "ALL",
    content: "",
    status: "DRAFT"
  });

  const { admindetails } = useSelector((state) => state.SubAdmin);

  useEffect(() => {
    if (id) {
      setLoading(true);
      Api.get(`admin/kb/${id}`)
        .then((res) => {
          if (res.data?.status_code === 200) {
            setFormData(res.data.data);
          }
        })
        .catch(err => {
          toast.error("Failed to fetch article details");
        })
        .finally(() => setLoading(false));
    }
  }, [id]);

  /**
   * Strips HTML tags from content to provide plain text for Pinecone embeddings
   */
  const stripHtml = (html) => {
    const tmp = document.createElement("DIV");
    tmp.innerHTML = html;
    // Replace block-level elements with spaces to prevent word concatenation
    const text = tmp.textContent || tmp.innerText || "";
    return text.trim();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      toast.error("Article title is required");
      return;
    }
    if (!formData.content.trim() || formData.content === "<p><br></p>") {
      toast.error("Article content cannot be empty");
      return;
    }

    const payload = {
      ...formData,
      plainText: stripHtml(formData.content)
    };

    setLoading(true);
    const action = id ? updateKbArticle({ id, data: payload }) : createKbArticle(payload);
    
    dispatch(action)
      .unwrap()
      .then(() => {
        navigate("/knowledge-base");
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  // Advanced toolbar configuration for corporate styling
  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, 4, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      [{ 'align': [] }],
      ['blockquote', 'code-block'],
      ['link', 'image'],
      ['clean']
    ],
  };

  return (
    <Layout>
      <LoadingOverlay active={loading} spinner text="Saving Knowledge Article...">
        <div className="dashboard_data_wrap">
          <div className="dashbord_cont_wrapper">
            <div className="modern-user-section-card shadow-sm border-0 p-4" style={{ background: "#fff", borderRadius: 16 }}>
              <div className="d-flex justify-content-between align-items-center mb-4 border-bottom pb-3">
                <div>
                  <h4 className="mb-1" style={{ color: "#034328", fontWeight: 700 }}>
                    {id ? "Edit Knowledge Article" : "Contribute New Article"}
                  </h4>
                  <p className="text-muted small mb-0">Use the rich text editor to draft high-signal documentation.</p>
                </div>
                <button className="btn btn-outline-secondary px-4 rounded-pill" onClick={() => navigate("/knowledge-base")}>
                  Cancel
                </button>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="row g-4 mb-4">
                  <div className="col-md-8">
                    <label className="form-label fw-bold text-dark small uppercase tracking-wider">Article Title</label>
                    <input
                      type="text"
                      className="form-control form-control-lg border-gray-200 focus-emerald"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      placeholder="Enter a descriptive title..."
                      required
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label fw-bold text-dark small uppercase tracking-wider">Domain Target</label>
                    <select
                      className="form-select form-select-lg border-gray-200"
                      value={formData.targetDomain}
                      onChange={(e) => setFormData({ ...formData, targetDomain: e.target.value })}
                    >
                      <option value="ALL">General (All Domains)</option>
                      <option value="BUSINESS">Business Ecosystem</option>
                      <option value="CONSUMER">Consumer Platform</option>
                      <option value="WEBSITE">Public Website</option>
                    </select>
                  </div>
                </div>

                <div className="row g-4 mb-4">
                  <div className="col-md-6">
                    <label className="form-label fw-bold text-dark small uppercase tracking-wider">Category</label>
                    <input
                      type="text"
                      className="form-control border-gray-200"
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      placeholder="e.g. Policies, Tech, Support"
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-bold text-dark small uppercase tracking-wider">Publication Status</label>
                    <select
                      className="form-select border-gray-200"
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                      disabled={admindetails?.role !== "Admin" && id && formData.status === "PUBLISHED"}
                    >
                      <option value="DRAFT">Save as Draft</option>
                      {(admindetails?.role === "Admin" || formData.status === "PUBLISHED") && (
                        <option value="PUBLISHED">Publish to Semantic Search</option>
                      )}
                    </select>
                  </div>
                </div>

                <div className="mb-5">
                  <label className="form-label fw-bold text-dark small uppercase tracking-wider">Article Content</label>
                  <div style={{ minHeight: 450, marginBottom: 60 }} className="quill-wrapper shadow-sm rounded">
                    <ReactQuill
                      theme="snow"
                      value={formData.content}
                      onChange={(val) => setFormData({ ...formData, content: val })}
                      modules={quillModules}
                      placeholder="Start writing your article here..."
                      style={{ height: 400 }}
                    />
                  </div>
                </div>

                <div className="d-flex justify-content-end gap-3 border-top pt-4">
                  <button type="button" className="btn btn-light px-4" onClick={() => navigate("/knowledge-base")}>
                    Discard
                  </button>
                  <button type="submit" className="common-btn px-5 shadow">
                    {id ? "Save Changes" : (formData.status === "PUBLISHED" ? "Publish Now" : "Submit Draft")}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </LoadingOverlay>
    </Layout>
  );
}
